import React, { useState, useMemo } from 'react';

export const CustomUnorderedList = ({ children, ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const listId = useMemo(() => `list-ul-${Math.random().toString(36).substr(2, 9)}`, []);
  
  // Filter out any null, undefined, or empty children
  const childArray = React.Children.toArray(children).filter(child => {
    if (!child) return false;
    // If it's a string with only whitespace, exclude it
    if (typeof child === 'string' && !child.trim()) return false;
    return true;
  });
  
  const totalItems = childArray.length;
  const maxItemsToShow = 10;
  const shouldTruncate = totalItems > maxItemsToShow;
  const displayedChildren = shouldTruncate && !isExpanded ? childArray.slice(0, maxItemsToShow) : childArray;
  const remainingItems = totalItems - maxItemsToShow;
  
  return (
    <div className="pl-1 py-1 my-1">
      <ul className="dark:text-gray-200 text-gray-700 list-disc list-outside ml-6 pl-2 mb-1 space-y-1.5" {...props}>
        {displayedChildren}
      </ul>
      {shouldTruncate && (
        <div className="mt-2 text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-300 ease-out transform hover:scale-105"
          >
            {isExpanded ? (
              <>
                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">Show Less</span>
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                  View more items ({remainingItems} more)
                </span>
                <div className="relative w-4 h-4 flex flex-col items-center justify-center overflow-hidden">
                  <div className="arrow-sliding">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-2">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-3">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export const CustomOrderedList = ({ children, ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const listId = useMemo(() => `list-ol-${Math.random().toString(36).substr(2, 9)}`, []);
  
  // Filter out any null, undefined, or empty children
  const childArray = React.Children.toArray(children).filter(child => {
    if (!child) return false;
    // If it's a string with only whitespace, exclude it
    if (typeof child === 'string' && !child.trim()) return false;
    return true;
  });
  
  const totalItems = childArray.length;
  const maxItemsToShow = 10;
  const shouldTruncate = totalItems > maxItemsToShow;
  const displayedChildren = shouldTruncate && !isExpanded ? childArray.slice(0, maxItemsToShow) : childArray;
  const remainingItems = totalItems - maxItemsToShow;
  
  return (
    <div className="pl-1 py-1 my-1">
      <ol className="dark:text-gray-200 text-gray-700 list-decimal list-outside ml-6 pl-2 mb-1 space-y-1.5" {...props}>
        {displayedChildren}
      </ol>
      {shouldTruncate && (
        <div className="mt-2 text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-300 ease-out transform hover:scale-105"
          >
            {isExpanded ? (
              <>
                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">Show Less</span>
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                  View more items ({remainingItems} more)
                </span>
                <div className="relative w-4 h-4 flex flex-col items-center justify-center overflow-hidden">
                  <div className="arrow-sliding">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-2">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div className="arrow-sliding arrow-delay-3">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

